import { EventEmitter, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { HttpClient } from "@angular/common/http";
import {
    ActivityContribution, ActivityHighlight,
    CommentYourMoodContribution,
    KudosHighlight,
    MoodStreakHighlight, PopularCommentContribution,
    TopicHighlight,
    UserActivity
} from "./user-activity.types";
import { catchError, firstValueFrom, map, of, ReplaySubject } from "rxjs";
import { format } from "date-fns";
import { KudosRecipient, QuestionType } from "@teammood/timeline";
import { template } from "lodash";

@Injectable({
    providedIn: 'root'
})
export class UserActivityService {

    constructor(private http: HttpClient, private translate: TranslateService) { }

    async getActivityData(teamId: string): Promise<UserActivity> {
        return await firstValueFrom(
            this.http.get<UserActivityDTO>('/api/' + teamId + '/activity').pipe(
                map(response => {
                    return this.transformActivity(response);
                }),
                catchError(err => {
                    return of(null)
                })
            )
        );
    }

    transformActivity(activityDTO: UserActivityDTO): UserActivity{
        const popularComments = this.sortAndCutActivities(activityDTO.contributions.filter(item => item.type === "popular-comment"));
        const transformedPopularComments = this.transformPopularComments(popularComments as PopularCommentContribution[]);

        const commentYourMood: CommentYourMoodContribution[] = this.sortAndCutActivities(activityDTO.contributions.filter(item => item.type === "comment-your-mood"));

        const activeBadMoodStreaks = this.sortAndCutActivities(activityDTO.highlights.filter(item => item.type === "badMoodStreakItem"));
        const transformedBadMoodStreaks = this.transformBadMoodStreaks(activeBadMoodStreaks as MoodStreakHighlight[])

        const activeTopics = this.sortAndCutActivities(activityDTO.highlights.filter(item => item.type === "topicItem"));
        const transformedActiveTopics = this.transformActiveTopics(activeTopics as TopicHighlight[]);

        const activeKudos = this.sortAndCutActivities(activityDTO.highlights.filter(item => item.type === "kudosItem"));
        const transformedActiveKudos = this.transformActiveKudos(activeKudos as KudosHighlight[]);

        return {
            popularComments: [...transformedPopularComments],
            commentYourMood: commentYourMood,
            activeBadMoodStreaks: [...transformedBadMoodStreaks],
            activeTopics: [...transformedActiveTopics],
            activeKudos: [...transformedActiveKudos],
            topicRequest: {
                show: false
            },
            questionRequest: {
                show: false
            },
            issueRequest: {
                show: false
            },
            kudosRequest: {
                show: true
            }
        };
    }

    formatActivityLink(textContent: string){
        let transformedContent = textContent;
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const urls = this.findLinksInText(textContent, urlRegex);
        if(urls !== null && urls.length > 0){
            const patterns = this.transformUrlsInPatterns(urls);
            patterns.forEach((pattern, index) => {
                transformedContent = transformedContent.replace(urls[index], pattern);
            })
        }
        return transformedContent;
    }

    findLinksInText(text: string, regex: RegExp = /(https?:\/\/[^\s]+)/g){
        return text.match(regex);
    }

    transformUrlsInPatterns(urls: string[]){
        return urls.map(url => {
            if(url.includes('.gif')){
                //return '[Animated Picture]'; // used for test
                return this.translate.instant('user_activity.link_pattern.gif');
            }else{
                return this.translate.instant('user_activity.link_pattern.link');
                //return '[Link]'; // used for test
            }
        })
    }

    transformPopularComments(comments: PopularCommentContribution[]): PopularCommentContribution[]{
        return comments.map(comment => {
            comment.commentWithFormattedLinks = this.formatActivityLink(comment.comment);
            return comment;
        });
    }

    transformBadMoodStreaks(streaks: MoodStreakHighlight[]){
        return streaks.map(item => {
            item.dateAsString = format(item.date, 'EEE dd MMM');
            return item;
        });
    }

    sortAndCutActivities(activities: ActivityContribution[] | ActivityHighlight[]){
        const sortedActivities = activities.sort((a, b) => b.date - a.date);
        return sortedActivities.length > 3 ? sortedActivities.slice(0, 3) : sortedActivities;
    }

    transformActiveTopics(topics: TopicHighlight[]){
        return topics.map(topic => {
            topic.titleWithFormattedLinks = this.formatActivityLink(topic.title);
            return topic;
        });
    }

    transformActiveKudos(kudos: KudosHighlight[]){
        return kudos.map(kudos => {
            kudos.titleWithFormattedLinks = this.formatActivityLink(kudos.title);
            return kudos;
        });
    }

    async getTeamMatesList(teamId: string): Promise<KudosRecipient[]> {
        const response = await firstValueFrom(this.http.get<KudosRecipient[]>('/api/team/' + teamId + '/members'));
        return this.orderTeamMatesList(response);
    }

    private orderTeamMatesList(teamMatesList: KudosRecipient[]): KudosRecipient[]{
        return teamMatesList.sort((a, b) => {
            const nameA = a.fullname.toLowerCase(), nameB = b.fullname.toLowerCase();
            if (nameA < nameB) //sort string ascending
                return -1;
            if (nameA > nameB)
                return 1;
            return 0; //default return value (no sorting)
        });
    }

    getKudosTemplates(){
        return [
            {
                type: 'thanks',
                templates: [
                    'kudos.thanks.placeholder.1',
                    'kudos.thanks.placeholder.2',
                    'kudos.thanks.placeholder.3',
                    'kudos.thanks.placeholder.4',
                    'kudos.thanks.placeholder.5',
                    'kudos.thanks.placeholder.6',
                ]
            },
            {
                type: 'congratulate',
                templates: [
                    'kudos.congratulate.placeholder.1',
                    'kudos.congratulate.placeholder.2',
                    'kudos.congratulate.placeholder.3',
                    'kudos.congratulate.placeholder.4',
                    'kudos.congratulate.placeholder.5',
                ]
            },
            {
                type: 'celebrate',
                templates: [
                    'kudos.celebrate.placeholder.1',
                    'kudos.celebrate.placeholder.2',
                    'kudos.celebrate.placeholder.3',
                    'kudos.celebrate.placeholder.4',
                    'kudos.celebrate.placeholder.5',
                    'kudos.celebrate.placeholder.6',
                ]
            }
        ]
    }

    allQuestionSuggestions():QuestionSuggestion[] {
        return [
            {
                type: 'team_spirit',
                typeName: 'topic.team_spirit',
                placeholders: [
                    'topic.team_spirit.placeholder.1',
                    'topic.team_spirit.placeholder.2',
                    'topic.team_spirit.placeholder.3',
                    'topic.team_spirit.placeholder.4',
                    'topic.team_spirit.placeholder.5',
                    'topic.team_spirit.placeholder.6',
                    'topic.team_spirit.placeholder.7',
                    'topic.team_spirit.placeholder.8',
                ]
            },
            {
                type: 'management',
                typeName: 'topic.management',
                placeholders: [
                    'topic.management.placeholder.1',
                    'topic.management.placeholder.2',
                    'topic.management.placeholder.3',
                    'topic.management.placeholder.4',
                    'topic.management.placeholder.5',
                ]
            },
            {
                type: 'remote_work',
                typeName: 'topic.remote',
                placeholders: [
                    'topic.remote.placeholder.1',
                    'topic.remote.placeholder.2',
                    'topic.remote.placeholder.3',
                    'topic.remote.placeholder.4',
                    'topic.remote.placeholder.5',
                ]
            },
            {
                type: 'fun',
                typeName: 'topic.fun',
                placeholders: [
                    'topic.fun.placeholder.1',
                    'topic.fun.placeholder.2',
                    'topic.fun.placeholder.3',
                    'topic.fun.placeholder.4',
                    'topic.fun.placeholder.5',
                    'topic.fun.placeholder.6',
                    'topic.fun.placeholder.7',
                    'topic.fun.placeholder.8',
                    'topic.fun.placeholder.9',
                ]
            },
            {
                type: 'agile',
                typeName: 'topic.agile',
                placeholders: [
                    'topic.agile.placeholder.1',
                    'topic.agile.placeholder.2',
                    'topic.agile.placeholder.3',
                    'topic.agile.placeholder.4',
                ]
            },
            {
                type: 'about_teammood',
                typeName: 'topic.about_teammood',
                placeholders: [
                    'topic.about_teammood.placeholder.1',
                    'topic.about_teammood.placeholder.2',
                    'topic.about_teammood.placeholder.3',
                    'topic.about_teammood.placeholder.4',
                    'topic.about_teammood.placeholder.5',
                    'topic.about_teammood.placeholder.6',
                ]
            }

        ]
    }

    shakeKudos$: EventEmitter<void> = new EventEmitter()
    shakeKudos() {
        this.shakeKudos$.emit();
    }
}

interface UserActivityDTO { // from the backend
    contributions: ActivityContribution[],
    highlights: ActivityHighlight[]
}

export interface QuestionSuggestion {
    type: QuestionType,
    typeName: string,
    placeholders: string[]
}